import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import {
  ChildTableColumn,
  ChildTableConfig,
  ChildTableRow,
  ChildTableState,
  CellPosition,
  EditingCell,
  FilterConfig,
  SortConfig,
  ValidationRule,
} from '../models/child-table.models';
import { validateCell } from '../core/validators';

/**
 * Child Table State Service
 * Framework-agnostic state management for the spreadsheet table
 * Can be adapted to React using hooks or Zustand
 */
@Injectable()
export class ChildTableStateService<T = any> {
  private state$ = new BehaviorSubject<ChildTableState<T>>(this.getInitialState());
  private changes$ = new Subject<{ rowId: string; columnKey: string; value: any }>();
  private config: ChildTableConfig<T> | null = null;

  /** Observable of the full state */
  readonly state: Observable<ChildTableState<T>> = this.state$.asObservable();

  /** Observable of filtered and sorted rows */
  readonly displayRows$: Observable<ChildTableRow<T>[]> = this.state$.pipe(
    map((state) => this.computeDisplayRows(state)),
    distinctUntilChanged()
  );

  /** Observable of selected row IDs */
  readonly selectedIds$: Observable<string[]> = this.state$.pipe(
    map((state) => Array.from(state.selectedIds)),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  /** Observable for auto-save with debounce */
  readonly saveChanges$: Observable<ChildTableRow<T>[]>;

  constructor() {
    this.saveChanges$ = this.changes$.pipe(
      debounceTime(500),
      map(() => this.getDirtyRows())
    );
  }

  /**
   * Initialize the table with configuration.
   * @param pageSize Optional page size override. If omitted, preserves the
   *   current state's pageSize (so config-only refreshes don't reset it).
   *   Falls back to 10 on first initialization.
   */
  initialize(config: ChildTableConfig<T>, data: T[] = [], pageSize?: number): void {
    this.config = config;
    const rows = this.createRows(data, config.idField || 'id' as keyof T & string);
    const prevPageSize = this.state$.getValue().pageSize;

    this.state$.next({
      ...this.getInitialState(),
      rows,
      columns: config.columns,
      pageSize: pageSize ?? prevPageSize ?? 10,
    });
  }

  /**
   * Get current state snapshot
   */
  getState(): ChildTableState<T> {
    return this.state$.getValue();
  }

  /**
   * Update rows data
   */
  setRows(data: T[]): void {
    const idField = this.config?.idField || 'id' as keyof T & string;
    const rows = this.createRows(data, idField);
    this.updateState({ rows });
  }

  /**
   * Add a new row (at end by default, or at a specific index)
   */
  addRow(data?: Partial<T>, atIndex?: number): ChildTableRow<T> {
    const state = this.getState();
    const newId = this.generateId();
    const newRow: ChildTableRow<T> = {
      id: newId,
      data: { ...this.getDefaultRowData(), ...data } as T,
      selected: false,
      dirty: true,
    };

    if (atIndex !== undefined && atIndex >= 0 && atIndex <= state.rows.length) {
      const newRows = [...state.rows];
      newRows.splice(atIndex, 0, newRow);
      this.updateState({ rows: newRows });
    } else {
      this.updateState({ rows: [...state.rows, newRow] });
    }

    return newRow;
  }

  /**
   * Look up a row by ID.
   */
  getRow(rowId: string): ChildTableRow<any> | undefined {
    return this.getState().rows.find((r) => r.id === rowId);
  }

  /**
   * Clear all editable cell values in a row
   */
  clearRow(rowId: string): void {
    const state = this.getState();
    const rowIndex = state.rows.findIndex((r) => r.id === rowId);
    if (rowIndex === -1) return;

    const row = state.rows[rowIndex];
    const clearedData: any = { ...row.data };
    for (const col of state.columns) {
      if (!col.editable) continue;
      switch (col.type) {
        case 'number':
        case 'currency':
          clearedData[col.key] = null;
          break;
        case 'checkbox':
          clearedData[col.key] = false;
          break;
        default:
          clearedData[col.key] = '';
      }
    }

    const newRows = [...state.rows];
    newRows[rowIndex] = {
      ...row,
      data: clearedData,
      originalData: row.originalData || { ...row.data },
      dirty: true,
    };
    this.updateState({ rows: newRows });
  }

  /**
   * Delete rows by IDs
   */
  deleteRows(ids: string[]): void {
    const state = this.getState();
    const idSet = new Set(ids);
    
    this.updateState({
      rows: state.rows.filter((row) => !idSet.has(row.id)),
      selectedIds: new Set([...state.selectedIds].filter((id) => !idSet.has(id))),
    });
  }

  /**
   * Update a cell value
   */
  updateCell(rowId: string, columnKey: string, value: any): void {
    const state = this.getState();
    const rowIndex = state.rows.findIndex((r) => r.id === rowId);
    
    if (rowIndex === -1) return;
    
    const row = state.rows[rowIndex];
    const originalData = row.originalData || { ...row.data };
    const newData = { ...row.data, [columnKey]: value };
    
    const newRows = [...state.rows];
    newRows[rowIndex] = {
      ...row,
      data: newData,
      originalData,
      dirty: true,
    };
    
    this.updateState({ rows: newRows });
    this.changes$.next({ rowId, columnKey, value });
  }

  /**
   * Validate a cell value
   */
  validateCell(rowId: string, columnKey: string, value: any): string | null {
    const column = this.getState().columns.find((c) => c.key === columnKey);
    if (!column?.validation) return null;
    
    const row = this.getState().rows.find((r) => r.id === rowId);
    
    return validateCell(column.validation, value, row?.data);
  }

  /**
   * Set cell validation error
   */
  setCellError(rowId: string, columnKey: string, error: string | null): void {
    const state = this.getState();
    const rowIndex = state.rows.findIndex((r) => r.id === rowId);
    
    if (rowIndex === -1) return;
    
    const row = state.rows[rowIndex];
    const newErrors = { ...row.errors };
    
    if (error) {
      newErrors[columnKey] = error;
    } else {
      delete newErrors[columnKey];
    }
    
    const newRows = [...state.rows];
    newRows[rowIndex] = { ...row, errors: newErrors };
    
    this.updateState({ rows: newRows });
  }

  /**
   * Toggle row selection
   */
  toggleRowSelection(rowId: string): void {
    const state = this.getState();
    const newSelectedIds = new Set(state.selectedIds);
    
    if (this.config?.selectionMode === 'single') {
      newSelectedIds.clear();
    }
    
    if (newSelectedIds.has(rowId)) {
      newSelectedIds.delete(rowId);
    } else {
      newSelectedIds.add(rowId);
    }
    
    this.lastSelectedRowIndex = null; // reset anchor after simple toggle
    this.updateState({ selectedIds: newSelectedIds });
  }

  /** Index of the last checkbox-clicked row, used for Shift+click range selection. */
  lastSelectedRowIndex: number | null = null;

  /**
   * Toggle row selection with optional Shift-key range extension.
   * When shiftKey is true and lastSelectedRowIndex is set, selects all rows
   * between the previous anchor and the current row.
   */
  toggleRowSelectionWithShift(
    rowId: string,
    rowIndex: number,
    shiftKey: boolean,
    displayRows: ChildTableRow<T>[],
  ): void {
    if (this.config?.selectionMode === 'single') {
      this.toggleRowSelection(rowId);
      this.lastSelectedRowIndex = rowIndex;
      return;
    }

    const state = this.getState();
    const newSelectedIds = new Set(state.selectedIds);

    if (shiftKey && this.lastSelectedRowIndex !== null) {
      const from = Math.min(this.lastSelectedRowIndex, rowIndex);
      const to = Math.max(this.lastSelectedRowIndex, rowIndex);
      for (let i = from; i <= to; i++) {
        const row = displayRows[i];
        if (row) newSelectedIds.add(row.id);
      }
    } else {
      if (newSelectedIds.has(rowId)) {
        newSelectedIds.delete(rowId);
      } else {
        newSelectedIds.add(rowId);
      }
      this.lastSelectedRowIndex = rowIndex;
    }

    this.updateState({ selectedIds: newSelectedIds });
  }

  resetSelectionAnchor(): void {
    this.lastSelectedRowIndex = null;
  }

  /**
   * Select all visible rows
   */
  selectAll(): void {
    const displayRows = this.computeDisplayRows(this.getState());
    const newSelectedIds = new Set(displayRows.map((r) => r.id));
    this.updateState({ selectedIds: newSelectedIds });
  }

  /**
   * Clear all selections
   */
  clearSelection(): void {
    this.updateState({ selectedIds: new Set() });
  }

  /**
   * Set sort configuration
   */
  setSort(sort: SortConfig | null): void {
    this.updateState({ sort });
  }

  /**
   * Toggle sort on a column
   */
  toggleSort(columnKey: string): void {
    const state = this.getState();
    const column = state.columns.find((c) => c.key === columnKey);
    
    if (!column?.sortable) return;
    
    let newSort: SortConfig | null;
    
    if (state.sort?.column === columnKey) {
      if (state.sort.direction === 'asc') {
        newSort = { column: columnKey, direction: 'desc' };
      } else {
        newSort = null;
      }
    } else {
      newSort = { column: columnKey, direction: 'asc' };
    }
    
    this.updateState({ sort: newSort });
  }

  /**
   * Set filters
   */
  setFilters(filters: FilterConfig[]): void {
    this.updateState({ filters });
  }

  /**
   * Add a filter
   */
  addFilter(filter: FilterConfig): void {
    const state = this.getState();
    this.updateState({ filters: [...state.filters, filter] });
  }

  /**
   * Remove a filter
   */
  removeFilter(columnKey: string): void {
    const state = this.getState();
    this.updateState({
      filters: state.filters.filter((f) => f.column !== columnKey),
    });
  }

  /**
   * Set search term
   */
  setSearchTerm(term: string): void {
    this.updateState({ searchTerm: term });
  }

  /**
   * Start editing a cell
   */
  startEditing(rowId: string, columnKey: string): void {
    const state = this.getState();
    const row = state.rows.find((r) => r.id === rowId);
    const column = state.columns.find((c) => c.key === columnKey);
    
    if (!row || !column?.editable) return;
    
    this.updateState({
      editingCell: {
        rowId,
        columnKey,
        originalValue: (row.data as any)[columnKey],
      },
    });
  }

  /**
   * Stop editing
   */
  stopEditing(): void {
    this.updateState({ editingCell: null });
  }

  /**
   * Cancel editing and restore original value
   */
  cancelEditing(): void {
    const state = this.getState();
    if (!state.editingCell) return;
    
    const { rowId, columnKey, originalValue } = state.editingCell;
    this.updateCell(rowId, columnKey, originalValue);
    this.stopEditing();
  }

  /**
   * Set focused cell position
   */
  setFocusedCell(position: CellPosition | null): void {
    this.updateState({ focusedCell: position });
  }

  /**
   * Move row to a new position
   */
  moveRow(fromIndex: number, toIndex: number): void {
    const state = this.getState();
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= state.rows.length || toIndex >= state.rows.length) return;
    
    const newRows = [...state.rows];
    const [movedRow] = newRows.splice(fromIndex, 1);
    newRows.splice(toIndex, 0, movedRow);
    
    this.updateState({ rows: newRows });
  }

  /**
   * Move column to a new position
   */
  moveColumn(fromIndex: number, toIndex: number): void {
    const state = this.getState();
    if (fromIndex === toIndex) return;
    
    const newColumns = [...state.columns];
    const [movedColumn] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, movedColumn);
    
    this.updateState({ columns: newColumns });
  }

  /**
   * Get dirty (unsaved) rows
   */
  getDirtyRows(): ChildTableRow<T>[] {
    return this.getState().rows.filter((r) => r.dirty);
  }

  /**
   * Mark specific rows as dirty so they are picked up by the next autosave cycle.
   * Used by the embedded component to re-sync rows after a parent record is created.
   */
  markRowsDirty(rowIds: string[]): void {
    if (rowIds.length === 0) return;
    const idSet = new Set(rowIds);
    const state = this.getState();
    let changed = false;
    const newRows = state.rows.map((row) => {
      if (idSet.has(row.id) && !row.dirty) {
        changed = true;
        return { ...row, dirty: true };
      }
      return row;
    });
    if (changed) {
      this.updateState({ rows: newRows });
      this.changes$.next({ rowId: rowIds[0], columnKey: '', value: null });
    }
  }

  /**
   * Mark all rows as clean (saved)
   */
  markAllClean(): void {
    const state = this.getState();
    const newRows = state.rows.map((row) => ({
      ...row,
      dirty: false,
      originalData: undefined,
    }));
    this.updateState({ rows: newRows });
  }

  /**
   * Replace temp row IDs with real database IDs after a successful sync.
   * This prevents re-editing a synced row from being treated as "new".
   */
  replaceRowIds(mapping: Record<string, string>): void {
    const state = this.getState();
    let changed = false;
    const newRows = state.rows.map((row) => {
      const realId = mapping[row.id];
      if (realId) {
        changed = true;
        return { ...row, id: realId };
      }
      return row;
    });
    if (changed) {
      this.updateState({ rows: newRows });
    }
  }

  /**
   * Revert a specific row to its originalData (undo a failed edit).
   * If the row has no originalData, it's left unchanged.
   */
  revertRow(rowId: string): void {
    const state = this.getState();
    const rowIndex = state.rows.findIndex((r) => r.id === rowId);
    if (rowIndex === -1) return;

    const row = state.rows[rowIndex];
    if (!row.originalData) return;

    const newRows = [...state.rows];
    newRows[rowIndex] = {
      ...row,
      data: { ...row.originalData },
      originalData: undefined,
      dirty: false,
      errors: undefined,
    };
    this.updateState({ rows: newRows });
  }

  /**
   * Set page size
   */
  setPageSize(size: number): void {
    this.updateState({ pageSize: size, currentPage: 0 });
  }

  /**
   * Set current page
   */
  setCurrentPage(page: number): void {
    this.updateState({ currentPage: page });
  }

  // Private methods

  private getInitialState(): ChildTableState<T> {
    return {
      rows: [],
      columns: [],
      selectedIds: new Set(),
      sort: null,
      filters: [],
      editingCell: null,
      focusedCell: null,
      searchTerm: '',
      pageSize: 10,
      currentPage: 0,
    };
  }

  private updateState(partial: Partial<ChildTableState<T>>): void {
    this.state$.next({ ...this.state$.getValue(), ...partial });
  }

  private createRows(data: T[], idField: keyof T & string): ChildTableRow<T>[] {
    return data.map((item, index) => ({
      id: String((item as any)[idField] ?? index),
      data: item,
      selected: false,
      dirty: false,
    }));
  }

  private generateId(): string {
    return `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getDefaultRowData(): Partial<T> {
    const defaults: any = {};
    for (const column of this.getState().columns) {
      switch (column.type) {
        case 'number':
          defaults[column.key] = null; // Empty for new rows
          break;
        case 'currency':
          defaults[column.key] = null; // Empty for new rows, shows as $0 in display
          break;
        case 'checkbox':
          defaults[column.key] = false;
          break;
        case 'text':
        default:
          defaults[column.key] = '';
          break;
      }
    }
    return defaults;
  }

  private computeDisplayRows(state: ChildTableState<T>): ChildTableRow<T>[] {
    let result = [...state.rows];
    
    // Apply search — skip null/undefined/boolean to avoid false positives
    // (e.g. String(false) = "false" which contains "a")
    if (state.searchTerm) {
      const term = state.searchTerm.toLowerCase();
      result = result.filter((row) =>
        state.columns.some((col) => {
          const value = (row.data as any)[col.key];
          if (value == null || typeof value === 'boolean') return false;
          return String(value).toLowerCase().includes(term);
        })
      );
    }
    
    // Apply filters
    for (const filter of state.filters) {
      result = result.filter((row) =>
        this.matchesFilter((row.data as any)[filter.column], filter)
      );
    }
    
    // Apply sort
    if (state.sort) {
      const { column, direction } = state.sort;
      result.sort((a, b) => {
        const aVal = (a.data as any)[column];
        const bVal = (b.data as any)[column];
        
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        const aStr = String(aVal ?? '').toLowerCase();
        const bStr = String(bVal ?? '').toLowerCase();
        const comparison = aStr.localeCompare(bStr);
        return direction === 'asc' ? comparison : -comparison;
      });
    }
    
    return result;
  }

  private matchesFilter(value: any, filter: FilterConfig): boolean {
    const strValue = String(value ?? '').toLowerCase();
    const filterValue = String(filter.value ?? '').toLowerCase();
    
    switch (filter.operator) {
      case 'equals':
        return strValue === filterValue;
      case 'notEquals':
        return strValue !== filterValue;
      case 'contains':
        return strValue.includes(filterValue);
      case 'notContains':
        return !strValue.includes(filterValue);
      case 'startsWith':
        return strValue.startsWith(filterValue);
      case 'endsWith':
        return strValue.endsWith(filterValue);
      case 'greaterThan':
        return Number(value) > Number(filter.value);
      case 'lessThan':
        return Number(value) < Number(filter.value);
      case 'isEmpty':
        return !value || strValue === '';
      case 'isNotEmpty':
        return !!value && strValue !== '';
      default:
        return true;
    }
  }
}
